"use client";

import { ConfirmAction, WithPermission } from "@atlas/composite";
import { type PaginationState, useToast } from "@atlas/core";
import { FilterBuilder, usePermission, useTranslation } from "@atlas/sdk";
import { CdvType } from "@enums";
import type { IHubCdv } from "@models";
import { useDeleteHubCdv, useSearchHubCdvs } from "@query";
import { HUB_PERM } from "@shared";
import { useCallback, useMemo, useState } from "react";
import { CdvEditDialog } from "./CdvEditDialog";
import { CdvManagerGroupsGrid } from "./CdvManagerGroupsGrid";

export function CdvManagerPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { isPermission: hasCdvManage } = usePermission([HUB_PERM.CDV_MANAGE]);

  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  });
  const filter = useMemo(() => {
    const builder = new FilterBuilder();
    if (searchQuery.trim()) {
      builder.contains("label", searchQuery.trim());
    }
    return builder.build();
  }, [searchQuery]);

  const { data: cdvsData, isLoading } = useSearchHubCdvs(
    {
      filter,
      mode: "DATA",
      startIndex: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
    },
    { enabled: hasCdvManage },
  );
  const cdvs = useMemo(
    () =>
      cdvsData?.pages.flatMap((page: { items: IHubCdv[] }) => page.items) ?? [],
    [cdvsData],
  );

  const { mutate: deleteCdv } = useDeleteHubCdv();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<IHubCdv | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IHubCdv | null>(null);

  const openCreate = useCallback(() => {
    setEditing(null);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((row: IHubCdv) => {
    setEditing(row);
    setDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    if (deleteTarget?.id == null) return;
    deleteCdv(deleteTarget.id, {
      onSuccess: () => {
        setDeleteTarget(null);
        toast.add({
          title: t("hub.cdv_manager.delete_success", {
            defaultValue: "CDV deleted",
          }),
          data: { type: "success" },
        });
      },
      onError: () => {
        toast.add({
          title: t("hub.cdv_manager.delete_error", {
            defaultValue: "Could not delete CDV",
          }),
          data: { type: "error" },
        });
      },
    });
  }, [deleteTarget, deleteCdv, toast, t]);

  const typeSelectOptions = useMemo((): { value: string; label: string }[] => {
    const labelFor = (type: string) => {
      const key = `hub.cdv_manager.type_${type.toLowerCase()}`;
      return t(key as any, { defaultValue: type });
    };
    const map = new Map<string, string>();
    for (const c of cdvs) {
      const ty = c.type?.trim();
      if (ty) map.set(ty, labelFor(ty));
    }
    Object.values(CdvType).forEach((type) => {
      if (!map.has(type)) map.set(type, labelFor(type));
    });
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [cdvs, t]);

  return (
    <WithPermission permissions={[HUB_PERM.CDV_MANAGE]}>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <CdvManagerGroupsGrid
          cdvs={cdvs}
          isLoading={isLoading}
          hasCdvManage={hasCdvManage}
          onCreateClick={openCreate}
          onEdit={openEdit}
          onRequestDelete={setDeleteTarget}
          onSearchChange={setSearchQuery}
          pagination={pagination}
          onPaginationChange={setPagination}
        />

        <CdvEditDialog
          isLoadingCdvs={isLoading}
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          editing={editing}
          typeSelectOptions={typeSelectOptions}
        />

        <ConfirmAction
          open={!!deleteTarget}
          onOpenChange={(open: boolean) => !open && setDeleteTarget(null)}
          title={t("hub.cdv_manager.delete_confirm_title", {
            defaultValue: "Delete CDV?",
          })}
          description={t("hub.cdv_manager.delete_confirm_description", {
            name:
              deleteTarget?.label ??
              deleteTarget?.name ??
              t("hub.cdv_manager.this_cdv", { defaultValue: "this CDV" }),
            defaultValue:
              'Delete "{{name}}"? Catalog forms may reference this value.',
          })}
          confirmText={t("hub.cdv_manager.delete", { defaultValue: "Delete" })}
          cancelText={t("hub.administer.cancel", { defaultValue: "Cancel" })}
          actionVariant="destructive"
          onConfirm={handleDeleteConfirm}
        />
      </div>
    </WithPermission>
  );
}
